import React from 'react';
import PropTypes from 'prop-types';

import {Paper, Typography, Stack, Timeline} from 'views/components';
import {HistoryOutlined as HistoryOutlinedIcon} from 'views/icons';
import {Translate} from 'views/module';

import RecordCreation from '../timeline/RecordCreation';
import ShiftClockedIn from '../timeline/ShiftClockedIn';
import ShiftClockedOut from '../timeline/ShiftClockedOut';
import ShiftConfirmed from '../timeline/ShiftConfirmed';
import ShiftMissedAction from '../timeline/ShiftMissedAction';
import ShiftRequestedCancel from '../timeline/ShiftRequestedCancel';
import ShiftRescheduled from '../timeline/ShiftRescheduled';
import ShiftRequestedReschedule from '../timeline/ShiftRequestedReschedule';
import ShiftCancelled from '../timeline/ShiftCancelled';

const EventTimelineTile = (props) => {
  const {event, setModel} = props;

  const [timeline, setTimeline] = React.useState([]);

  const caregiverUpdates = event.caregiverUpdates;

  React.useEffect(() => {
    const timelineEvents = [
      {
        date: event.createdAt,
        element: <RecordCreation createdAt={event.createdAt} key="created" />,
      },
    ];

    for (const caregiverUpdate of caregiverUpdates) {
      if (caregiverUpdate.type === 'shiftConfirmation') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftConfirmed
            confirmation={caregiverUpdate}
            caregiverData={event.caregiverData}
            key="shiftConfirmed"
          />,
        });
      } else if (caregiverUpdate.type === 'clockInConfirmation') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftClockedIn clockIn={caregiverUpdate}
            caregiverData={event.caregiverData}
            key="clockIn"
            setModel={setModel}
          />,
        });
      } else if (caregiverUpdate.type === 'clockOutConfirmation') {
        const answers = caregiverUpdate.data.answers;
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftClockedOut
            clockOut={caregiverUpdate}
            caregiverData={event.caregiverData}
            answers={answers}
            key="clockOut"
            setModel={setModel}
          />,
        });
      } else if (caregiverUpdate.type === 'shiftConfirmationMissed') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftMissedAction
            update={caregiverUpdate}
            caregiverData={event.caregiverData}
            titleExpression='schedule.eventDetail.EventTimelineSection.ShiftConfirmationMissed.Title'
            descriptionExpression='schedule.eventDetail.EventTimelineSection.ShiftConfirmationMissed.Description'
            iconColor='#f9ab00'
            key='shiftConfirmationMissed'
          />,
        });
      } else if (caregiverUpdate.type === 'clockInConfirmationMissed') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftMissedAction
            update={caregiverUpdate}
            caregiverData={event.caregiverData}
            titleExpression='schedule.eventDetail.EventTimelineSection.ShiftClockInMissed.Title'
            descriptionExpression='schedule.eventDetail.EventTimelineSection.ShiftClockInMissed.Description'
            iconColor='#f57c00'
            key='clockInConfirmationMissed'
          />,
        });
      } else if (caregiverUpdate.type === 'clockOutConfirmationMissed') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftMissedAction
            update={caregiverUpdate}
            caregiverData={event.caregiverData}
            titleExpression='schedule.eventDetail.EventTimelineSection.ShiftClockOutMissed.Title'
            descriptionExpression='schedule.eventDetail.EventTimelineSection.ShiftClockOutMissed.Description'
            iconColor='#ea4335'
            key='clockOutConfirmationMissed'
          />,
        });
      } else if (caregiverUpdate.type === 'requestReschedule') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
            <ShiftRequestedReschedule
              reschedule={caregiverUpdate}
              caregiverData={event.caregiverData}
              setModel={setModel}
              key="requestReschedule"
              status={event.status}
            />,
        });
      } else if (caregiverUpdate.type === 'requestCancel') {
        timelineEvents.push({
          date: caregiverUpdate.createdAt,
          element:
          <ShiftRequestedCancel
            cancellation={caregiverUpdate}
            caregiverData={event.caregiverData}
            setModel={setModel}
            key="requestCancel"
            status={event.status}
          />,
        });
      }
    }

    if (event.status === 'DEACTIVATED') {
      if (event.deactivatedData.newRescheduleEventRn) {
        timelineEvents.push({
          date: event.deactivatedData.timeDeactivated,
          element:
          <ShiftRescheduled
            deactivatedData={event.deactivatedData}
            key="reschedule"
          />,
        });
      } else {
        timelineEvents.push({
          date: event.deactivatedData.timeDeactivated,
          element:
          <ShiftCancelled
            deactivatedData={event.deactivatedData}
            key="cancel"
          />,
        });
      }
    }

    const sortedTimeline = timelineEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

    setTimeline(sortedTimeline);
  }, [caregiverUpdates]);


  return (
    <Paper sx={{p: 2, mb: 2, borderRadius: 4}}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <HistoryOutlinedIcon sx={{width: 24, height: 24, color: 'grey'}} />
        <Typography variant="h6">
          <Translate expression='schedule.eventDetail.EventTimelineSection.Title' />
        </Typography>
      </Stack>
      <Timeline
        position="right"
      >
        {timeline.map((e) => e.element)}
      </Timeline>
    </Paper>
  );
};

EventTimelineTile.propTypes = {
  event: PropTypes.object.isRequired,
  setModel: PropTypes.func.isRequired,
};

export default EventTimelineTile;
